import { sidebarPage } from '../utils/sidebarPageLayout.js';
import { auth, db } from '../../src/firebase.js';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Agency Component
 * A modern, "New Era" brochure for the NIL Athlete Ecosystem.
 */
export class Agency {
    constructor(containerId) {
        this.containerId = containerId;
        this.currentStoryIndex = 0;
        this.stories = [
            { name: "Elena Rodriguez", sport: "Forward, Liga F", achievement: "340% increase in brand deal value", quote: "The AI Matchmaking tool found sponsors I never would have reached on my own. It changed my career trajectory.", image: "https://images.unsplash.com/photo-1612459284970-e8f027596582?q=80&w=800" },
            { name: "Sarah Jenkins", sport: "Midfielder, WSL", achievement: "Launched sold-out 'SJ8' Apparel Line", quote: "Vertical integration meant I kept 80% of my merch profits. The Agency handled everything from POD to shipping.", image: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?q=80&w=800" },
            { name: "Ayo Thompson", sport: "Rising Star, NWFL", achievement: "50k+ Podcast Listeners", quote: "The Media Powerhouse gave me a voice. Brands now come to me because they see the engagement in my private fan club.", image: "https://images.unsplash.com/photo-1621503928135-263309192931?q=80&w=800" }
        ];
    }

    init() {
        const el = document.getElementById(this.containerId);
        if (!el) return;

        this.injectStyles();

        const main = `
            <div class="fga-agency-container">
                <!-- Hero Section -->
                <header class="fga-hero">
                    <div class="fga-hero__badge">New Era Agency</div>
                    <h1 class="fga-hero__title">NIL: The 360° Athlete Ecosystem</h1>
                    <p class="fga-hero__subtitle">Where Talent Meets Technology, Media, and Commerce</p>
                    <div class="fga-hero__intro">
                        Welcome to the future of NIL. We aren't just an agency; we are a full-scale media network and tech incubator designed specifically for the modern female athlete. We don't wait for brands to call—we build the platforms that make them compete for you.
                    </div>
                </header>

                <!-- Main Pillars Grid -->
                <div class="fga-section-grid">
                    <div class="fga-card fga-animate-up">
                        <div class="fga-card__icon">🎙️</div>
                        <h2 class="fga-card__title">01. The Media Powerhouse</h2>
                        <p class="fga-card__text">We own the airwaves and the screens. Your brand is broadcasted, not just posted.</p>
                        <ul class="fga-card__list">
                            <li><strong>NIL Radio & Podcast Network:</strong> Professional-grade storytelling with national ad slots.</li>
                            <li><strong>Live Stream Channel:</strong> PPV workshops and Live Shopping events.</li>
                        </ul>
                    </div>

                    <div class="fga-card fga-animate-up" style="animation-delay: 0.1s">
                        <div class="fga-card__icon">🤖</div>
                        <h2 class="fga-card__title">02. The Tech Stack</h2>
                        <p class="fga-card__text">We use proprietary AI and interactive tools to scale your influence 24/7.</p>
                        <ul class="fga-card__list">
                            <li><strong>NIL AI Chatbot:</strong> Automates brand matching and strategy tracking.</li>
                            <li><strong>VIP Messaging:</strong> Monetized private fan clubs and exclusive content.</li>
                            <li><strong>Gamified Engagement:</strong> NIL-themed mini-games to win store discounts.</li>
                        </ul>
                    </div>

                    <div class="fga-card fga-animate-up" style="animation-delay: 0.2s">
                        <div class="fga-card__icon">⚡</div>
                        <h2 class="fga-card__title">03. Performance & Wellness</h2>
                        <p class="fga-card__text">Your expertise is your product. We turn your routine into revenue.</p>
                        <ul class="fga-card__list">
                            <li><strong>Digital Fitness Portal:</strong> Subscription-based training and nutrition plans.</li>
                            <li><strong>Sponsored Challenges:</strong> Brand-partnered 30-day challenges for lead gen.</li>
                        </ul>
                    </div>

                    <div class="fga-card fga-animate-up" style="animation-delay: 0.3s">
                        <div class="fga-card__icon">🛍️</div>
                        <h2 class="fga-card__title">04. Commerce & Merchandise</h2>
                        <p class="fga-card__text">The store is always open. No inventory, no hassle, just results.</p>
                        <ul class="fga-card__list">
                            <li><strong>Athlete Pro-Shop:</strong> Fully integrated e-commerce and Print-on-Demand.</li>
                            <li><strong>Fan Boxes:</strong> Bundled physical merch with "Golden Tickets" to your media.</li>
                        </ul>
                    </div>
                </div>

                <!-- Strategy Section -->
                <section class="fga-results">
                    <h2 class="fga-section-heading">How We Deliver Results</h2>
                    <div class="fga-results__grid">
                        <div class="fga-results__item">
                            <h4>Vertical Integration</h4>
                            <p>We don't outsource. We own the store, the radio, and the tech. Higher margins (we keep commissions between 10–20%).</p>
                        </div>
                        <div class="fga-results__item">
                            <h4>Omni-Channel Revenue</h4>
                            <p>7 streams of income simultaneously: Ads, Subs, IAP, Courses, Merch, Affiliate, and Licensing.</p>
                        </div>
                        <div class="fga-results__item">
                            <h4>Data-Driven Growth</h4>
                            <p>Our AI tools capture real fan data, proving to brands exactly why you are their best investment.</p>
                        </div>
                    </div>
                </section>

                <!-- Success Stories Slider -->
                <section class="fga-stories">
                    <h2 class="fga-section-heading">Success Stories</h2>
                    <div class="fga-stories__slider">
                        <div class="fga-stories__track" id="fga-stories-track">
                            ${this.stories.map(s => `
                                <div class="fga-story-card">
                                    <div class="fga-story-card__img" style="background-image: url('${s.image}')"></div>
                                    <div class="fga-story-card__content">
                                        <span class="fga-story-card__stat">${s.achievement}</span>
                                        <p class="fga-story-card__quote">"${s.quote}"</p>
                                        <div class="fga-story-card__author">
                                            <strong>${s.name}</strong>
                                            <span>${s.sport}</span>
                                        </div>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                        <button class="fga-slider-btn fga-slider-btn--prev" id="fga-prev">←</button>
                        <button class="fga-slider-btn fga-slider-btn--next" id="fga-next">→</button>
                    </div>
                </section>

                <!-- Santiago Merlot Executive Section -->
                <section class="fga-executive fga-animate-up">
                    <div class="fga-executive__badge">Executive Tier</div>
                    <h2 class="fga-executive__title">The Merlot Method</h2>
                    <p class="fga-executive__intro">
                        Leveraging 30+ years of expertise from <strong>Santiago Merlot</strong> (CEO of footballgirl), we provide high-ticket strategic consulting and automated legacy products.
                    </p>
                    <div class="fga-executive__services">
                        <div class="fga-exec-card">
                            <h5>Strategic Consulting</h5>
                            <p>Strategy on infrastructure and investment for pro clubs and organizations.</p>
                            <span class="fga-price">$1,500 - $5,000 / session</span>
                        </div>
                        <div class="fga-exec-card">
                            <h5>Digital Mastercourse</h5>
                            <p>"The Merlot Method": Self-paced mastery on Women's Sports Strategy & AI Media.</p>
                            <span class="fga-price">$497 - $997 (One-time)</span>
                        </div>
                        <div class="fga-exec-card">
                            <h5>Executive Retainer</h5>
                            <p>Fractional Chief Growth Officer services for major brands entering the NIL space.</p>
                            <span class="fga-price">$5,000+ / month</span>
                        </div>
                    </div>
                </section>

                <!-- Pricing Table -->
                <section class="fga-pricing fga-animate-up">
                    <h2 class="fga-section-heading">Automated Revenue Breakdown</h2>
                    <div class="fga-table-wrapper">
                        <table class="fga-pricing-table">
                            <thead>
                                <tr>
                                    <th>Service</th>
                                    <th>Price Point</th>
                                    <th>Automation Level</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>NIL AI Assistant</td>
                                    <td>$49/mo</td>
                                    <td><span class="fga-tag fga-tag--full">Full (SaaS)</span></td>
                                </tr>
                                <tr>
                                    <td>Podcast Ad Network</td>
                                    <td>$250 - $750/spot</td>
                                    <td><span class="fga-tag fga-tag--high">High (Dynamic)</span></td>
                                </tr>
                                <tr>
                                    <td>Merlot Strategy Course</td>
                                    <td>$497 - $997</td>
                                    <td><span class="fga-tag fga-tag--full">Full (Digital)</span></td>
                                </tr>
                                <tr>
                                    <td>VIP Fan Messaging</td>
                                    <td>$10 - $30/mo</td>
                                    <td><span class="fga-tag fga-tag--med">Medium</span></td>
                                </tr>
                                <tr>
                                    <td>Fitness Portal</td>
                                    <td>$25/mo</td>
                                    <td><span class="fga-tag fga-tag--high">High (Pre-recorded)</span></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <footer class="fga-cta">
                    <button class="fg-btn fg-btn--primary" id="fga-apply-btn">Apply to Join the Roster</button>
                    <button class="fg-btn" id="fga-partner-btn">Partner with Our Media Network</button>
                </footer>
            </div>
        `;

        el.innerHTML = sidebarPage({
            title: 'Agency',
            subtitle: 'Professional Representation for the Digital Age.',
            main,
            wide: true
        });

        this.attachEvents();
    }

    async handleApplication() {
        const user = auth.currentUser;
        const googleFormUrl = "https://docs.google.com/forms/d/e/YOUR_FORM_ID/viewform"; // Replace with your actual ID

        // 1. Log to Firebase (Audit Trail)
        if (user) {
            try {
                await addDoc(collection(db, 'agency_leads'), {
                    uid: user.uid,
                    email: user.email,
                    type: 'roster_application',
                    timestamp: serverTimestamp()
                });
            } catch (e) { console.error("Firebase log failed", e); }
        }

        // 2. Log to Google Analytics (Global Tag)
        if (typeof gtag === 'function') {
            gtag('event', 'roster_application_click', { 'user_email': user?.email || 'guest' });
        }

        // 3. Redirect to Google Form
        window.open(googleFormUrl, '_blank');
    }

    injectStyles() {
        const styleId = 'fga-agency-styles';
        if (document.getElementById(styleId)) return;
        const css = `
            .fga-agency-container { max-width: 1000px; margin: 0 auto; text-align: left; font-family: inherit; color: #111; padding-bottom: 80px; }
            
            /* Hero */
            .fga-hero { text-align: center; padding: 60px 20px; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border-radius: 24px; margin-bottom: 40px; }
            .fga-hero__badge { display: inline-block; padding: 6px 12px; background: var(--primary-blue); color: #fff; border-radius: 99px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 20px; }
            .fga-hero__title { font-size: 3rem; font-weight: 900; line-height: 1.1; margin-bottom: 16px; letter-spacing: -1px; }
            .fga-hero__subtitle { font-size: 1.25rem; font-weight: 500; color: #64748b; margin-bottom: 24px; }
            .fga-hero__intro { max-width: 700px; margin: 0 auto; font-size: 1.1rem; line-height: 1.6; color: #475569; }

            /* Cards Grid */
            .fga-section-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 60px; }
            .fga-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 32px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
            .fga-card:hover { transform: translateY(-8px); border-color: var(--primary-blue); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); }
            .fga-card__icon { font-size: 2.5rem; margin-bottom: 20px; }
            .fga-card__title { font-size: 1.5rem; font-weight: 800; margin-bottom: 12px; }
            .fga-card__text { color: #64748b; margin-bottom: 20px; line-height: 1.5; }
            .fga-card__list { list-style: none; padding: 0; }
            .fga-card__list li { padding: 8px 0; border-top: 1px solid #f1f5f9; font-size: 0.95rem; color: #475569; }

            /* Results Section */
            .fga-results { padding: 40px; background: #1e293b; border-radius: 24px; color: #fff; margin-bottom: 60px; }
            .fga-section-heading { font-size: 2rem; font-weight: 800; margin-bottom: 32px; text-align: center; }
            .fga-results__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
            .fga-results__item h4 { color: #38bdf8; font-size: 1.1rem; margin-bottom: 12px; font-weight: 700; }
            .fga-results__item p { font-size: 0.9rem; line-height: 1.6; opacity: 0.8; }

            /* Stories Slider */
            .fga-stories { margin-bottom: 60px; }
            .fga-stories__slider { position: relative; overflow: hidden; border-radius: 24px; background: #fff; border: 1px solid #e2e8f0; }
            .fga-stories__track { display: flex; transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
            .fga-story-card { min-width: 100%; display: grid; grid-template-columns: 40% 60%; align-items: center; }
            .fga-story-card__img { height: 400px; background-size: cover; background-position: center; }
            .fga-story-card__content { padding: 48px; }
            .fga-story-card__stat { display: inline-block; padding: 4px 12px; background: #dcfce7; color: #166534; border-radius: 99px; font-size: 0.85rem; font-weight: 700; margin-bottom: 20px; }
            .fga-story-card__quote { font-size: 1.5rem; font-weight: 700; font-style: italic; line-height: 1.4; color: #1e293b; margin-bottom: 24px; }
            .fga-story-card__author strong { display: block; font-size: 1.1rem; color: #1e293b; }
            .fga-story-card__author span { color: #64748b; font-size: 0.9rem; }
            
            .fga-slider-btn { position: absolute; top: 50%; transform: translateY(-50%); width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,0.9); border: 1px solid #e2e8f0; cursor: pointer; z-index: 10; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
            .fga-slider-btn:hover { background: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
            .fga-slider-btn--prev { left: 16px; }
            .fga-slider-btn--next { right: 16px; }

            /* Executive Section */
            .fga-executive { padding: 48px; border: 2px solid #fbbf24; border-radius: 24px; background: #fffbeb; margin-bottom: 60px; position: relative; overflow: hidden; }
            .fga-executive::after { content: 'MERLOT'; position: absolute; right: -20px; bottom: -20px; font-size: 8rem; font-weight: 900; color: #fbbf24; opacity: 0.05; pointer-events: none; }
            .fga-executive__badge { color: #b45309; font-weight: 800; font-size: 0.75rem; text-transform: uppercase; margin-bottom: 12px; }
            .fga-executive__title { font-size: 2.5rem; font-weight: 900; color: #92400e; margin-bottom: 16px; }
            .fga-executive__intro { max-width: 600px; font-size: 1.1rem; color: #b45309; margin-bottom: 32px; }
            .fga-executive__services { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
            .fga-exec-card { background: #fff; padding: 24px; border-radius: 16px; border: 1px solid #fde68a; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
            .fga-exec-card h5 { font-size: 1rem; font-weight: 800; margin-bottom: 8px; }
            .fga-exec-card p { font-size: 0.85rem; color: #6b7280; margin-bottom: 16px; line-height: 1.4; }
            .fga-price { display: block; font-weight: 700; color: #b45309; font-size: 0.9rem; }

            /* Table */
            .fga-table-wrapper { overflow-x: auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; }
            .fga-pricing-table { width: 100%; border-collapse: collapse; }
            .fga-pricing-table th { background: #f8fafc; text-align: left; padding: 16px 24px; font-size: 0.85rem; font-weight: 700; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; }
            .fga-pricing-table td { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; font-size: 0.95rem; }
            .fga-tag { font-size: 0.75rem; font-weight: 700; padding: 4px 10px; border-radius: 99px; }
            .fga-tag--full { background: #dcfce7; color: #166534; }
            .fga-tag--high { background: #e0f2fe; color: #075985; }
            .fga-tag--med { background: #fef3c7; color: #92400e; }

            /* CTA */
            .fga-cta { display: flex; gap: 16px; justify-content: center; padding-top: 40px; }
            .fga-cta .fg-btn { width: auto; min-width: 240px; padding: 16px 32px; font-size: 1.1rem; }

            /* Animation */
            .fga-animate-up { animation: fgaUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; opacity: 0; }
            @keyframes fgaUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

            @media (max-width: 768px) {
                .fga-hero__title { font-size: 2rem; }
                .fga-section-grid, .fga-results__grid, .fga-executive__services, .fga-story-card { grid-template-columns: 1fr; }
                .fga-story-card__img { height: 250px; }
                .fga-cta { flex-direction: column; }
                .fga-cta .fg-btn { width: 100%; }
            }
        `;
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    attachEvents() {
        const track = document.getElementById('fga-stories-track');
        const prev = document.getElementById('fga-prev');
        const next = document.getElementById('fga-next');
        const applyBtn = document.getElementById('fga-apply-btn');

        const updateSlider = () => {
            track.style.transform = `translateX(-${this.currentStoryIndex * 100}%)`;
        };

        next?.addEventListener('click', () => {
            this.currentStoryIndex = (this.currentStoryIndex + 1) % this.stories.length;
            updateSlider();
        });

        prev?.addEventListener('click', () => {
            this.currentStoryIndex = (this.currentStoryIndex - 1 + this.stories.length) % this.stories.length;
            updateSlider();
        });

        applyBtn?.addEventListener('click', () => this.handleApplication());
    }
}